<template>
    <template v-for="deploymentHolder in deploymentHolders" :key="getKey(deploymentHolder)">
        <TitleRow v-for="titleRow in titlesBeforeResource(deploymentHolder)" :title="titleRow" :key="'titleBefore|' + titleRow.id" stickyText />
        <DeploymentsHolderRow
            :renderedDateRange="renderedDateRange"
            :groupLabels="groupLabels"
            :resource="isDeploymentHolderAResource(deploymentHolder) ? deploymentHolder! : undefined"
            :organisationDeploymentStates="organisationDeploymentStates"
            :shifts="shifts"
            :timeBlocksWithoutWorkScheduleAssignmentsByDeploymentId="timeBlocksWithoutWorkScheduleAssignmentsByDeployments(deploymentHolder)"
            :deploymentsNotAvailableForDrop="deploymentsNotAvailableForDrop"
            :specificationsNotAvailableForDrop="specificationsNotAvailableForDrop"
            :workSchedulePositions="workSchedulePositions"
            :workScheduleAssignmentsByDeployment="workScheduleAssignmentsByDeployment"
            :workingDeploymentStates="workingDeploymentStates"
            :visibleDeploymentSpecificationTypeIds="visibleDeploymentSpecificationTypeIds"
            :rosterNames="rosterNames"
            :roster="roster"
            :otherRoster="isDeploymentHolderAResource(deploymentHolder) && deploymentHolder.contractedTimes?.[0].rosterId !== roster.rosterId"
            :company="isDeploymentHolderACompany(deploymentHolder) ? deploymentHolder : (isDeploymentHolderACompanyEmployee(deploymentHolder) ? companyForEmployee(deploymentHolder) : undefined)"
            :companyEmployee="isDeploymentHolderACompanyEmployee(deploymentHolder) ? deploymentHolder : undefined"
            @dragStart:workScheduleAssignment="$emit('dragStart:workScheduleAssignment', $event)"
            @dragEnd:workScheduleAssignment="$emit('dragEnd:workScheduleAssignment', $event)"
            @reload:workSchedulePosition="$emit('reload:workSchedulePosition', $event)"
        />
    </template>
    <TitleRow v-for="titleRow in titlesAfterResources" :title="titleRow" :key="'titleAfter|' + titleRow.id" stickyText />
</template>

<script lang="ts">
    import TitleRow from '@/components/WorkSchedule/TitleRow.vue';
    import DeploymentsHolderRow from '@/components/WorkSchedule/DeploymentsHolderRow.vue';
    import {PlanbordResource, ResourceWithKeyedByDateObjects, CompanyWithDeploymentsAndEmployees, CompanyEmployeeWithDeployments, PlanbordWorkSchedulePosition, PlanbordWorkScheduleGroupLabel} from '@/types/api-object-extensions';
    import {Title, Shift, DeploymentStateSpecification, OrganisationDeploymentState, Deployment, WorkScheduleAssignment} from '@cquential/api';
    import {RosterData, WorkScheduleDeploymentHolder, DateRange, RosterDay, WorkSchedulePreference} from '@/types/types';
    import {ResourceRosterRankUtil} from '@/util/ResourceRosterRankUtil';
    import WorkScheduleUtil from '@/util/WorkScheduleUtil';
    import {Component, Vue, Prop} from 'vue-facing-decorator';

    @Component({
        components: {
            TitleRow,
            DeploymentsHolderRow,
        },
        emits: [
            'dragStart:workScheduleAssignment',
            'dragEnd:workScheduleAssignment',
            'reload:workSchedulePosition',
        ],
    })
    export default class DeploymentHolders extends Vue {
        @Prop({required: true}) public renderedDateRange!: DateRange;
        @Prop({required: true}) public deploymentHolders!: WorkScheduleDeploymentHolder[];
        @Prop({required: true}) public deploymentsNotAvailableForDrop!: number[];
        @Prop({required: true}) public specificationsNotAvailableForDrop!: number[];
        @Prop({required: true}) public enabledCompanies!: CompanyWithDeploymentsAndEmployees[];
        @Prop({required: true}) public groupLabels!: Record<number, PlanbordWorkScheduleGroupLabel>;
        @Prop({required: true}) public organisationDeploymentStates!: Record<number, OrganisationDeploymentState>;
        @Prop({required: true}) public roster!: RosterData;
        @Prop({required: true}) public rosterNames!: Record<number, string>;
        @Prop({required: true}) public shifts!: Record<number, Shift>;
        @Prop({required: true}) public workScheduleAssignmentsByDeployment!: Record<number, WorkScheduleAssignment[]>;
        @Prop({required: true}) public workSchedulePositions!: Record<number, PlanbordWorkSchedulePosition>;
        @Prop({required: true}) public workingDeploymentStates!: DeploymentStateSpecification[];
        @Prop({required: true}) public visibleDeploymentSpecificationTypeIds!: number[];
        @Prop({required: true}) public deploymentsAvailableTimes!: Record<number, DateRange[]>;
        @Prop({required: true}) public workSchedulePreferences!: WorkSchedulePreference;

        public get maxRankInRoster(): number {
            return Math.max(
                ResourceRosterRankUtil.getHighestResourceRank(this.roster),
                ...this.roster.titles?.map((title: Title): number => title.rankInRoster) ?? [],
            );
        }

        public get renderedRosterDays(): RosterDay[] {
            return WorkScheduleUtil.convertDateRangeToRosterDays(this.renderedDateRange, this.roster);
        }

        public get hasResourcesFromOtherRosters(): boolean {
            return this.roster.resourcesFromOtherRosters.some((resource: ResourceWithKeyedByDateObjects): boolean => {
                return this.renderedRosterDays.some((day: RosterDay): boolean => day.formattedDateIso in resource.deployments && resource.deployments[day.formattedDateIso].length > 0);
            });
        }

        public get employeeSortingCanShowTitle(): boolean {
            return WorkScheduleUtil.employeeSortingCanShowTitle(this.workSchedulePreferences.employeeSorting);
        }

        public get titlesAfterResources(): Title[] {
            if (!this.roster || !this.roster.titles || !this.employeeSortingCanShowTitle || this.hasResourcesFromOtherRosters) {
                return [];
            }
            const highestResourceRank: number = ResourceRosterRankUtil.getHighestResourceRank(this.roster);

            return this.roster.titles.filter((title: Title): boolean => title.rankInRoster > highestResourceRank);
        }

        public get companiesById(): Record<number, CompanyWithDeploymentsAndEmployees> {
            const companies: Record<number, CompanyWithDeploymentsAndEmployees> = {};
            for (const company of this.enabledCompanies) {
                companies[company.id] = company;
            }

            return companies;
        }

        public titlesBeforeResource(resource: WorkScheduleDeploymentHolder): Title[] {
            if (!WorkScheduleUtil.isDeploymentHolderAResource(resource) || !this.roster || !this.roster.titles || !this.employeeSortingCanShowTitle) {
                return [];
            }
            const index: number = this.deploymentHolders.findIndex((rosterResource: WorkScheduleDeploymentHolder): boolean => WorkScheduleUtil.isDeploymentHolderAResource(rosterResource) && rosterResource.id === resource.id);
            let previousResourceRankInRoster: number;
            if (index <= 0) {
                previousResourceRankInRoster = -1;
            } else if ('rankInRoster' in this.deploymentHolders[index - 1]) {
                previousResourceRankInRoster = (this.deploymentHolders[index - 1] as PlanbordResource).rankInRoster;
            } else {
                previousResourceRankInRoster = this.maxRankInRoster;
            }

            const resourceRankInRoster: number = 'rankInRoster' in resource ? resource.rankInRoster : this.maxRankInRoster + 1;

            return this.roster.titles.filter((title: Title): boolean => title.rankInRoster > previousResourceRankInRoster && title.rankInRoster <= resourceRankInRoster);
        }

        public companyForEmployee(employee: CompanyEmployeeWithDeployments): CompanyWithDeploymentsAndEmployees {
            return this.companiesById[employee.companyId];
        }

        public timeBlocksWithoutWorkScheduleAssignmentsByDeployments(deploymentHolder: WorkScheduleDeploymentHolder): Record<number, DateRange[]> {
            return this.timeBlocksWithoutWorkScheduleAssignmentsByDeployment(WorkScheduleUtil.getDeploymentHolderDeployments(deploymentHolder));
        }

        public timeBlocksWithoutWorkScheduleAssignmentsByDeployment(deployments: Record<string, Deployment[]>): Record<number, DateRange[]> {
            const result: Record<number, DateRange[]> = {};
            for (const day of this.renderedRosterDays) {
                for (const deployment of deployments[day.formattedDateIso] ?? []) {
                    if (this.deploymentsAvailableTimes[deployment.id!]) {
                        result[deployment.id!] = this.deploymentsAvailableTimes[deployment.id!];
                    }
                }
            }

            return result;
        }

        public isDeploymentHolderAResource(deploymentHolder: WorkScheduleDeploymentHolder): deploymentHolder is PlanbordResource | ResourceWithKeyedByDateObjects {
            return WorkScheduleUtil.isDeploymentHolderAResource(deploymentHolder);
        }

        public isDeploymentHolderACompany(deploymentHolder: WorkScheduleDeploymentHolder): deploymentHolder is CompanyWithDeploymentsAndEmployees {
            return WorkScheduleUtil.isDeploymentHolderACompany(deploymentHolder);
        }

        public isDeploymentHolderACompanyEmployee(deploymentHolder: WorkScheduleDeploymentHolder): deploymentHolder is CompanyEmployeeWithDeployments {
            return WorkScheduleUtil.isDeploymentHolderACompanyEmployee(deploymentHolder);
        }

        public getKey(deploymentHolder: WorkScheduleDeploymentHolder): string {
            if (WorkScheduleUtil.isDeploymentHolderAResource(deploymentHolder)) {
                return 'resource|' + deploymentHolder.id!.toString();
            }
            if (WorkScheduleUtil.isDeploymentHolderACompany(deploymentHolder)) {
                return 'company|' + deploymentHolder.id.toString();
            }

            return 'deploymentHolder|' + deploymentHolder.id!.toString();
        }
    }
</script>
